import { create } from 'zustand';

import { saveLastActivity } from '../api/last-activity';
import { clearSession, loadSession, saveSession } from '../api/session-storage';
import type { Session } from '../types/auth.types';

/**
 * The session store — tokens only.
 *
 * ---------------------------------------------------------------------------
 * WHY ZUSTAND HERE, AND ONLY HERE
 *
 * This app's rule is that server state belongs to TanStack Query and Zustand is
 * reserved for genuinely global client-only state (§6, §10). The tokens
 * qualify: they are produced by login, held by the client, and read from
 * outside React — the axios interceptor attaches the access token to every
 * request, and interceptors cannot use context. `useAuthStore.getState()` is
 * readable from anywhere.
 *
 * Who the user is and what they may do is server state and lives in the
 * `['auth', 'me']` query (`current-user.queries.ts`), not here. Do not copy
 * role or permissions into this store.
 * ------------------------------------------------------------------------- */

export type AuthStatus = 'restoring' | 'authenticated' | 'unauthenticated';

/** Why the last session ended without the user signing out, for the login screen. */
export type SessionEndReason = 'expired' | 'idle';

interface AuthState {
  status: AuthStatus;
  session: Session | null;
  endReason: SessionEndReason | null;

  setSession: (session: Session) => void;
  clear: (reason?: SessionEndReason) => void;
  /** Rebuild the session from storage on a page load. */
  restore: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  // Starts as `restoring` so guards render a loading state instead of briefly
  // redirecting an authenticated user to the login page on refresh.
  status: 'restoring',
  session: null,
  endReason: null,

  setSession: (session) => {
    saveSession(session);
    // A fresh sign-in starts the idle clock, whatever an earlier session left behind.
    saveLastActivity();
    set({ session, status: 'authenticated', endReason: null });
  },

  clear: (reason) => {
    clearSession();
    set({ session: null, status: 'unauthenticated', endReason: reason ?? null });
  },

  restore: () => {
    // An expired access token is not fatal: the refresh token may still be
    // good, and the interceptor exchanges it on the first 401 (the `/admin/me`
    // call made right after restoring). A dead refresh token then ends the
    // session through the normal failure path.
    const session = loadSession();
    set(
      session ? { session, status: 'authenticated' } : { session: null, status: 'unauthenticated' },
    );
  },
}));

export function getRefreshToken(): string | null {
  return useAuthStore.getState().session?.refreshToken ?? null;
}
